import { rand, randNumber } from '@ngneat/falso';
import { TaxStrategy } from '@principle-theorem/accounting';
import { getServiceCodeNumberString } from '@principle-theorem/principle-core';
import {
  ADA_SERVICE_CODES,
  ChartableSurface,
  ChartedItemType,
  EventType,
  ParticipantType,
  PricingRuleType,
  ScopedSurface,
  ServiceCodeGroupType,
  ServiceCodeScope,
  ServiceCodeType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DimensionsCommon,
  FactsCommon,
  ISerialisedScopedServiceCode,
} from '@principle-theorem/reporting/interfaces';
import {
  BaseMock,
  MockGenerator,
  MockTimestamp,
  toSerialisedTimestamp,
  uid,
} from '@principle-theorem/shared';
import {
  AvroSerialisedVersionedSchemaMock,
  BigQueryNamedDocumentMock,
  SerialisedDocumentReferenceMock,
} from '../dimensions/common.mock';

export class BigQueryEventMock
  extends BaseMock<DimensionsCommon.IBigQueryEvent>
  implements DimensionsCommon.IBigQueryEvent
{
  from = toSerialisedTimestamp(MockTimestamp());
  to = toSerialisedTimestamp(MockTimestamp());
  practice = MockGenerator.generate(BigQueryNamedDocumentMock);
  creator = MockGenerator.generate(BigQueryNamedDocumentMock);
  organiser = MockGenerator.generate(BigQueryNamedDocumentMock);
  participants = [
    {
      ...MockGenerator.generate(BigQueryNamedDocumentMock),
      type: ParticipantType.Patient,
    },
    {
      ...MockGenerator.generate(BigQueryNamedDocumentMock),
      type: ParticipantType.Staffer,
    },
  ];
  type = EventType.Misc;
}

export class BigQueryGapMock
  extends BaseMock<FactsCommon.IBigQueryGap>
  implements FactsCommon.IBigQueryGap
{
  event = MockGenerator.generate(BigQueryEventMock);
  practitionerRef = MockGenerator.generate(SerialisedDocumentReferenceMock);
  practitioner = MockGenerator.generate(BigQueryNamedDocumentMock);
}

export class BigQueryChartedTreatmentMock
  extends BaseMock<FactsCommon.IBigQueryChartedTreatment>
  implements FactsCommon.IBigQueryChartedTreatment
{
  type = ChartedItemType.ChartedTreatment;
  scopeRef = { scope: ChartableSurface.WholeMouth as ScopedSurface };
  serviceCodeSmartGroups = [
    {
      uid: uid(),
      title: 'Smart Group',
      description: 'Mock smart group',
      scope: ServiceCodeScope.PerTooth,
      serviceCodes: [MockBigQueryPricedServiceCodeEntry()],
    },
  ];
  serviceCodeGroups = [
    {
      type: ServiceCodeGroupType.Required,
      serviceCodes: [MockBigQueryPricedServiceCodeEntry()],
    },
  ];
  serviceCodes = [MockBigQueryPricedServiceCodeEntry()];
  invoices = [
    {
      invoiceRef: MockGenerator.generate(SerialisedDocumentReferenceMock),
      lineItemUid: uid(),
      amount: randNumber({ min: 50, max: 500 }),
    },
  ];
  price = randNumber({ min: 50, max: 500 });
  basePrice = randNumber({ min: 50, max: 500 });
  feeSchedule = MockGenerator.generate(BigQueryNamedDocumentMock);
  attributedTo = MockGenerator.generate(BigQueryNamedDocumentMock);
  uuid = uid();
  config = MockGenerator.generate(BigQueryNamedDocumentMock);
  chartedSurfaces: FactsCommon.IBigQueryChartedTreatment['chartedSurfaces'] = [
    MockBigQueryChartedSurface(),
  ];
  resolvedBy = MockGenerator.generate(BigQueryNamedDocumentMock);
  resolvedAt = toSerialisedTimestamp(MockTimestamp());
  notes = [
    {
      uid: uid(),
      content: MockGenerator.generate(AvroSerialisedVersionedSchemaMock),
      immutable: false,
      owner: MockGenerator.generate(BigQueryNamedDocumentMock),
      createdAt: toSerialisedTimestamp(MockTimestamp()),
      updatedAt: toSerialisedTimestamp(MockTimestamp()),
    },
  ];
}

function MockBigQueryChartedSurface(): FactsCommon.IBigQueryChartedTreatment['chartedSurfaces'][0] {
  return {
    uuid: uid(),
    chartedBy: MockGenerator.generate(BigQueryNamedDocumentMock),
    chartedAt: toSerialisedTimestamp(MockTimestamp()),
    chartedRef: {},
  };
}

function MockBigQueryPricedServiceCodeEntry(): FactsCommon.IBigQueryPricedServiceCodeEntry {
  return {
    type: ServiceCodeType.ADA,
    code: randNumber({ min: 100, max: 999 }),
    quantity: randNumber({ min: 1, max: 3 }),
    pricingRule: {
      type: PricingRuleType.Flat,
      ruleItems: [
        {
          uid: uid(),
          price: randNumber({ min: 50, max: 500 }),
          startUnit: 1,
          selected: true,
        },
      ],
    },
    price: randNumber({ min: 50, max: 500 }),
    tax: randNumber({ min: 0, max: 50 }),
    taxStatus: TaxStrategy.GSTApplicable,
    chartedSurfaces: [MockBigQueryChartedSurface()],
  };
}

const serviceCode: ISerialisedScopedServiceCode = rand(
  ADA_SERVICE_CODES.map((code) => ({
    code: getServiceCodeNumberString(code.code),
    type: code.type,
  }))
);

export class SerialisedScopedServiceCodeMock
  extends BaseMock<ISerialisedScopedServiceCode>
  implements ISerialisedScopedServiceCode
{
  code = serviceCode.code;
  type = serviceCode.type;
}
